import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ── API call ──────────────────────────────────────────────────────────────────
const deleteMyAccount = async (password, confirmation) => {
  const token = localStorage.getItem('token');
  const res = await fetch('/api/users/me/delete', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ password, confirmation })
  });
  return res.json();
};

// ── Delete Account Modal ───────────────────────────────────────────────────────
const DeleteAccountModal = ({ onClose, onDeleted }) => {
  const [step, setStep] = useState(1); // 1=warning, 2=confirm, 3=deleting, 4=done
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setError('');
    if (confirmation !== 'DELETE MY ACCOUNT') {
      setError('Please type exactly: DELETE MY ACCOUNT');
      return;
    }
    setStep(3);
    try {
      const result = await deleteMyAccount(password, confirmation);
      if (result.success) {
        setStep(4);
        setTimeout(() => {
          // Clear all local storage
          localStorage.clear();
          onDeleted();
        }, 3000);
      } else {
        setStep(2);
        setError(result.error || 'Deletion failed. Please try again.');
      }
    } catch (err) {
      setStep(2);
      setError('Network error. Please try again.');
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: '20px'
    }}>
      <div style={{
        background: 'white', borderRadius: '12px', padding: '32px',
        maxWidth: '480px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>

        {/* Step 1 — Warning */}
        {step === 1 && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '48px' }}>⚠️</span>
            </div>
            <h2 style={{ color: '#DC2626', textAlign: 'center', marginBottom: '16px', fontSize: '22px' }}>
              Delete Your Account?
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.6', marginBottom: '16px' }}>
              This action is <strong>permanent and cannot be undone</strong>. The following will be deleted:
            </p>
            <ul style={{ color: '#475569', fontSize: '13px', lineHeight: '1.8', paddingLeft: '20px', marginBottom: '24px' }}>
              <li>Your profile and all personal information</li>
              <li>All SkillBot conversations and history</li>
              <li>Goals, roadmaps, and workspace</li>
              <li>Community connections and messages</li>
              <li>Uploaded documents and knowledge base entries</li>
              <li>LinkedIn network data</li>
              <li>All platform activity</li>
            </ul>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '12px', border: '1.5px solid #E2E8F0',
                  borderRadius: '6px', background: 'white', cursor: 'pointer',
                  fontSize: '14px', fontWeight: '600', color: '#475569'
                }}
              >
                Cancel — Keep Account
              </button>
              <button
                onClick={() => setStep(2)}
                style={{
                  flex: 1, padding: '12px', border: 'none',
                  borderRadius: '6px', background: '#DC2626', cursor: 'pointer',
                  fontSize: '14px', fontWeight: '600', color: 'white'
                }}
              >
                Continue to Delete
              </button>
            </div>
          </>
        )}

        {/* Step 2 — Confirm */}
        {step === 2 && (
          <>
            <h2 style={{ color: '#DC2626', marginBottom: '16px', fontSize: '20px' }}>
              Confirm Account Deletion
            </h2>
            {error && (
              <div style={{
                background: '#FEF2F2', border: '1px solid #FECACA',
                borderRadius: '6px', padding: '12px', marginBottom: '16px',
                color: '#DC2626', fontSize: '13px'
              }}>
                {error}
              </div>
            )}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                Your Password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                style={{
                  width: '100%', padding: '10px 14px', border: '1px solid #E2E8F0',
                  borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>
                Type <strong style={{ color: '#DC2626' }}>DELETE MY ACCOUNT</strong> to confirm
              </label>
              <input
                type="text"
                value={confirmation}
                onChange={e => setConfirmation(e.target.value)}
                placeholder="DELETE MY ACCOUNT"
                style={{
                  width: '100%', padding: '10px 14px',
                  border: `1px solid ${confirmation === 'DELETE MY ACCOUNT' ? '#10B981' : '#E2E8F0'}`,
                  borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
                  fontFamily: 'monospace', letterSpacing: '0.5px'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: '12px', border: '1.5px solid #E2E8F0',
                  borderRadius: '6px', background: 'white', cursor: 'pointer',
                  fontSize: '14px', fontWeight: '600', color: '#475569'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={confirmation !== 'DELETE MY ACCOUNT' || !password}
                style={{
                  flex: 1, padding: '12px', border: 'none',
                  borderRadius: '6px', fontSize: '14px', fontWeight: '600', color: 'white',
                  background: confirmation === 'DELETE MY ACCOUNT' && password ? '#DC2626' : '#FCA5A5',
                  cursor: confirmation === 'DELETE MY ACCOUNT' && password ? 'pointer' : 'not-allowed'
                }}
              >
                Permanently Delete
              </button>
            </div>
          </>
        )}

        {/* Step 3 — Deleting */}
        {step === 3 && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{
              width: '48px', height: '48px', border: '4px solid #E2E8F0',
              borderTopColor: '#06B6D4', borderRadius: '50%',
              animation: 'spin 1s linear infinite', margin: '0 auto 20px'
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <h3 style={{ color: '#1E293B', marginBottom: '8px' }}>Deleting Your Account...</h3>
            <p style={{ color: '#94A3B8', fontSize: '14px' }}>
              Removing all your data across all systems. Please wait.
            </p>
          </div>
        )}

        {/* Step 4 — Done */}
        {step === 4 && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <span style={{ fontSize: '56px' }}>✅</span>
            <h3 style={{ color: '#1E293B', marginTop: '16px', marginBottom: '8px' }}>
              Account Deleted
            </h3>
            <p style={{ color: '#94A3B8', fontSize: '14px' }}>
              All your data has been permanently removed.<br />
              A confirmation email has been sent to you.<br />
              Redirecting in a moment...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Settings Page ─────────────────────────────────────────────────────────────
const Settings = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeSection, setActiveSection] = useState('account');

  const handleDeleted = () => {
    logout();
    navigate('/login');
  };

  const sections = [
    { id: 'account', label: '👤 Account', icon: '👤' },
    { id: 'privacy', label: '🔒 Privacy', icon: '🔒' },
    { id: 'notifications', label: '🔔 Notifications', icon: '🔔' },
    { id: 'danger', label: '⚠️ Danger Zone', icon: '⚠️' }
  ];

  return (
    <div style={{ maxWidth: '860px', margin: '0 auto', padding: '32px 24px' }}>
      <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#0D2233', marginBottom: '8px' }}>
        Settings
      </h1>
      <p style={{ color: '#546E7A', marginBottom: '32px', fontSize: '15px' }}>
        Manage your account, privacy, and preferences
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '24px' }}>

        {/* Sidebar */}
        <div>
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '12px 16px', marginBottom: '4px',
                background: activeSection === s.id ? '#EBF9FA' : 'transparent',
                border: activeSection === s.id ? '1px solid #B2EBF2' : '1px solid transparent',
                borderRadius: '8px', cursor: 'pointer', fontSize: '14px',
                fontWeight: activeSection === s.id ? '600' : '400',
                color: activeSection === s.id ? '#00BCD4' : '#546E7A'
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #E0F7FA', padding: '28px' }}>

          {/* Account Section */}
          {activeSection === 'account' && (
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#0D2233', marginBottom: '20px' }}>
                Account Settings
              </h2>
              <p style={{ color: '#546E7A', fontSize: '14px', lineHeight: '1.6' }}>
                Manage your profile, email, and password from this section.
              </p>
              <div style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => navigate('/change-password')}
                  style={{
                    padding: '10px 20px', background: 'linear-gradient(135deg,#00BCD4,#2196F3)',
                    color: 'white', border: 'none', borderRadius: '6px',
                    cursor: 'pointer', fontSize: '14px', fontWeight: '600'
                  }}
                >
                  Change Password
                </button>
              </div>
            </div>
          )}

          {/* Privacy Section */}
          {activeSection === 'privacy' && (
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#0D2233', marginBottom: '20px' }}>
                Privacy Settings
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[
                  { label: 'Profile Visibility', desc: 'Who can see your profile', default: 'Platform members' },
                  { label: 'Roadmap Visibility', desc: 'Public roadmap for showcase / marketing', default: 'Private' },
                  { label: 'LinkedIn Network Data', desc: 'Your LinkedIn connections and analysis', default: 'Private — only you' },
                  { label: 'Knowledge Base Documents', desc: 'Your uploaded documents', default: 'Private — only you' },
                  { label: 'Showcase Posts', desc: 'Your knowledge showcase posts', default: 'Platform members' },
                  { label: 'Capability Card', desc: 'Your skills and availability', default: 'Platform members' }
                ].map(item => (
                  <div key={item.label} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 16px', background: '#F0FAFB', borderRadius: '8px',
                    border: '1px solid #E0F7FA'
                  }}>
                    <div>
                      <div style={{ fontWeight: '600', color: '#0D2233', fontSize: '14px' }}>{item.label}</div>
                      <div style={{ color: '#546E7A', fontSize: '12px', marginTop: '2px' }}>{item.desc}</div>
                    </div>
                    <div style={{
                      fontSize: '12px', color: '#00BCD4', fontWeight: '600',
                      background: '#E0F7FA', padding: '4px 10px', borderRadius: '100px'
                    }}>
                      {item.default}
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ color: '#94A3B8', fontSize: '12px', marginTop: '16px', lineHeight: '1.6' }}>
                Fine-grained privacy controls coming in the next update.
                Your data is always private by default.
              </p>
            </div>
          )}

          {/* Notifications Section */}
          {activeSection === 'notifications' && (
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#0D2233', marginBottom: '20px' }}>
                Notification Settings
              </h2>
              <p style={{ color: '#546E7A', fontSize: '14px', lineHeight: '1.6' }}>
                Notification preferences coming soon. You will be able to control:
                weekly digest, opportunity alerts, connection requests, and platform updates.
              </p>
            </div>
          )}

          {/* Danger Zone */}
          {activeSection === 'danger' && (
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#DC2626', marginBottom: '8px' }}>
                ⚠️ Danger Zone
              </h2>
              <p style={{ color: '#546E7A', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
                Actions here are permanent and cannot be undone.
              </p>

              <div style={{
                border: '1.5px solid #FECACA', borderRadius: '10px',
                padding: '24px', background: '#FEF2F2'
              }}>
                <h3 style={{ color: '#DC2626', fontSize: '16px', marginBottom: '8px' }}>
                  Delete Account
                </h3>
                <p style={{ color: '#7F1D1D', fontSize: '13px', lineHeight: '1.6', marginBottom: '16px' }}>
                  Permanently delete your account and all associated data.
                  This includes your profile, conversations, documents, connections,
                  goals, roadmaps, and all platform activity.
                  <strong> This cannot be undone.</strong>
                </p>
                <p style={{ color: '#7F1D1D', fontSize: '13px', lineHeight: '1.6', marginBottom: '20px' }}>
                  Your data will be removed from:
                  our databases, knowledge graph, AI context, and all backups within 24 hours.
                </p>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  style={{
                    padding: '11px 24px', background: '#DC2626', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '14px', fontWeight: '600'
                  }}
                >
                  Delete My Account Permanently
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
};

export default Settings;
